import { useQuery, useMutation, useApolloClient } from 'react-apollo';
import { useEffect } from 'react';

import { MeQuery } from '../../../../graphql/queries/account';
import {
  postNativeAppAlbumCreateError,
  postNativeAppAlbumCreateSuccess,
  postNativeAppAlbumFolderCreateError,
  postNativeAppAlbumFolderCreateSuccess,
  postNativeAppAlbumSlideshowUpdateError,
  postNativeAppAlbumSlideshowUpdateSuccess,
} from '../../../../services/native-bridge/native-apps-interface';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  albumCreate as albumCreateMutation,
  albumUpdate as albumUpdateMutation,
  albumDelete as albumDeleteMutation,
  albumSlideshowUpdate as albumSlideshowUpdateMutation,
} from '../../../../graphql/mutations/albums';
import {
  albumFolderCreate as albumFolderCreateMutation,
  albumFolderUpdate as albumFolderUpdateMutation,
} from '../../../../graphql/mutations/album-folder';
import { onCreateWeddingsAlbumFolder } from '../../views/AlbumFolder/WeddingEventCreateWizard/helpers';
import {
  AlbumFolderWithPurchasedEventAddons,
  MeForPurchasedEvents,
} from '../../../../graphql/queries/events';
import { processAlbumPostCheckout } from '../../views/AlbumFolder/FreemiumEventCreateWizard/helpers';
import { AlbumData } from '../../../../graphql/queries/albums';
import { ALBUM_SLIDESHOW_STATUS } from '../../../../constants/albums';

const useBridgeAPI = () => {
  const [albumCreate] = useMutation(albumCreateMutation);
  const [albumFolderCreate] = useMutation(albumFolderCreateMutation);
  const [albumFolderUpdate] = useMutation(albumFolderUpdateMutation);
  const [albumSlideshowUpdate] = useMutation(albumSlideshowUpdateMutation);
  const [albumUpdate] = useMutation(albumUpdateMutation);
  const [albumDelete] = useMutation(albumDeleteMutation);
  const { data: { me: user } = {} } = useQuery(MeQuery);
  const client = useApolloClient();

  const onWeddingEventCreate = async data => {
    try {
      const parsedData = typeof data === 'object' ? data : JSON.parse(data);
      const { values, purchasedEventId } = parsedData;

      const onAlbumCreate = async input => {
        return albumCreate({
          variables: {
            input,
          },
        });
      };

      const onAlbumFolderCreate = async input => {
        return albumFolderCreate({
          variables: {
            input,
          },
        });
      };

      const {
        data: { me: account },
      } = await client.query({
        query: MeForPurchasedEvents,
        variables: { unassigned_only: true },
      });

      const unassignedPurchasedEventRecord =
        account?.purchased_events?.records?.[0];

      const albumFolder = await onCreateWeddingsAlbumFolder({
        values,
        purchasedEventId:
          purchasedEventId || unassignedPurchasedEventRecord?.id,
        user,
        albumCreate: onAlbumCreate,
        albumFolderCreate: onAlbumFolderCreate,
      });

      postNativeAppAlbumFolderCreateSuccess({ albumFolderId: albumFolder?.id });
    } catch (err) {
      postNativeAppAlbumFolderCreateError(getErrorString(err));
    }
  };

  const onSpecialityEventCheckoutComplete = async data => {
    try {
      const parsedData = typeof data === 'object' ? data : JSON.parse(data);

      const {
        values,
        albumId,
        albumFolderId,
        purchasedEventAddonIds = [],
      } = parsedData;

      const onAlbumDelete = async input => {
        return albumDelete({
          variables: { input },
        });
      };
      const onAlbumUpdate = async input => {
        return albumUpdate({
          variables: { input },
        });
      };
      const onAlbumFolderUpdate = async input => {
        return albumFolderUpdate({
          variables: { input },
        });
      };
      const onAlbumCreate = async input => {
        return albumCreate({
          variables: {
            input,
          },
        });
      };

      if (albumFolderId && purchasedEventAddonIds?.length > 0) {
        // iOS use-case:
        await Promise.all(
          purchasedEventAddonIds.map(purchasedEventAddonId => {
            return onAlbumFolderUpdate({
              album_folder_id: albumFolderId,
              purchased_event_addon_id: purchasedEventAddonId,
            });
          }),
        );
      }

      const {
        data: { album },
      } = await client.query({
        query: AlbumData,
        variables: {
          id: albumId,
        },
      });

      const {
        data: { albumFolder },
      } = await client.query({
        query: AlbumFolderWithPurchasedEventAddons,
        variables: {
          id: album?.album_folder?.id,
        },
      });

      const purchasedEventAddons = albumFolder?.purchased_event_addons?.records;

      const {
        redirectToAlbumFolder,
        redirectAlbum,
      } = await processAlbumPostCheckout({
        values,
        album,
        user,
        eventType: album?.album_subtype ?? albumFolder?.album_subtype,
        purchasedEventAddons,
        albumCreate: onAlbumCreate,
        albumDelete: onAlbumDelete,
        albumUpdate: onAlbumUpdate,
        albumFolderUpdate: onAlbumFolderUpdate,
        includePhotosAlbum: values?.includePhotosAlbum,
      });

      if (redirectToAlbumFolder) {
        postNativeAppAlbumFolderCreateSuccess({
          albumFolderId: albumFolder?.id,
        });
      } else {
        postNativeAppAlbumCreateSuccess({ albumId: redirectAlbum?.id });
      }
    } catch (err) {
      postNativeAppAlbumCreateError(getErrorString(err));
    }
  };

  const onEventAddonItemsAssign = async data => {
    const parsedData = typeof data === 'object' ? data : JSON.parse(data);
    const { albumFolderId, purchasedEventAddonIds = [] } = parsedData;

    await Promise.all(
      purchasedEventAddonIds.map(purchasedEventAddonId => {
        return albumFolderUpdate({
          variables: {
            input: {
              album_folder_id: albumFolderId,
              purchased_event_addon_id: purchasedEventAddonId,
            },
          },
        });
      }),
    );
  };

  const onPublishSlideshow = async data => {
    const parsedData = typeof data === 'object' ? data : JSON.parse(data);
    const { albumId } = parsedData;

    const onAlbumSlideshowUpdate = async input => {
      return albumSlideshowUpdate({
        variables: { input },
      });
    };

    try {
      const {
        data: {
          albumUpdate: { album },
        },
      } = await onAlbumSlideshowUpdate({
        id: albumId,
        slideshow: {
          status: ALBUM_SLIDESHOW_STATUS.PUBLISHED,
        },
      });

      postNativeAppAlbumSlideshowUpdateSuccess({
        albumId,
        slideshow: { status: album.slideshow.status },
      });
    } catch (err) {
      postNativeAppAlbumSlideshowUpdateError(getErrorString(err));
    }
  };

  useEffect(() => {
    window.waldoApps = {
      ...window.waldoApps,
      onWeddingEventCreate,
      onSpecialityEventCheckoutComplete,
      onEventAddonItemsAssign,
      onPublishSlideshow,
    };
  }, []);
};

export default useBridgeAPI;
